import { CashFlow } from "components/BooksHomePage/BusinessOverview/CashFlow";
import html2canvas from "html2canvas";
import { openLink } from "utils/openLink";
import { useCurrentEntityId } from "hooks/useCurrentEntityId";
import { useCallback, useEffect, useRef } from "react";
import { useToast } from "hooks/useToast";
import {
  ForecastQueryParams,
  useExportFinancialSummaryMutation,
  useGetAccountsPayableAnalysisQuery,
  useGetAccountsReceivableAnalysisQuery,
  useGetAnalysisQuery,
  useGetCashBalanceAnalysisQuery,
  useGetMoMBreakdownQuery,
  useGetARAgingSummaryInsightsQuery,
  useGetAPAgingSummaryInsightsQuery,
  useGetFinancialMetricsQuery,
} from "store/apis/financialMetrics";
import {
  useGetMetricsQuery,
  useGetRevenueMetricsQuery,
} from "store/apis/metrics";
import { useLazyGetPreviewUrlQuery } from "store/apis/previewUrl";

import { AccountsPayable } from "./AccountsPayable";
import { AccountsReceivable } from "./AccountsReceivable";
import { APAgingCard, ARAgingCard } from "./AgingCard";
import { CashBalance } from "./CashBalance";
import { buildSummaryMetrics, SummaryMetricsCard } from "./FinancialSummary";
import { MoMBreakdown } from "./MoMBreakdown";
import { RevenueAnalysis } from "./RevenueAnalysis";
import { TopCustomers, TopVendors } from "./TopInsights";
import {
  DEFAULT_EXPORT_ANALYSIS_TYPES,
  ExportAnalysisTypes,
  GROUP_KEYS,
  SectionKey,
  getAllowedGroupOrder,
  getAllowedVisibleSections,
  isGroupVisible,
} from "constants/financialSummary";
import dayjs from "dayjs";
import { DISPLAY_DATE } from "constants/date";

const CAPTURE_DELAY_MS = 1000;

type Props = {
  onComplete: (success: boolean) => void;
  filter: { START_DATE: string; END_DATE: string };
  exportSections: Partial<Record<SectionKey, boolean>>;
  groupOrder: string[];
  periodText: string;
  groupId: string;
  isCustomDateRange?: boolean;
  analysisTypes?: ExportAnalysisTypes;
  onCaptured?: (base64Images: string[]) => void;
  isForecastEnabled?: boolean;
  forecastStartDate?: string;
  forecastEndDate?: string;
  assumptionSetId?: string;
};

export const FinancialSummaryPrint = ({
  onComplete,
  filter,
  exportSections: rawExportSections,
  groupOrder: rawGroupOrder,
  periodText,
  groupId,
  isCustomDateRange,
  analysisTypes = DEFAULT_EXPORT_ANALYSIS_TYPES,
  onCaptured,
  isForecastEnabled,
  forecastStartDate,
  forecastEndDate,
  assumptionSetId,
}: Props) => {
  const entityId = useCurrentEntityId();
  const { successToast } = useToast();

  const exportSections = getAllowedVisibleSections(
    rawExportSections,
    Boolean(isForecastEnabled)
  );
  const groupOrder = getAllowedGroupOrder(
    rawGroupOrder,
    Boolean(isForecastEnabled)
  );

  const forecastParams: ForecastQueryParams = isForecastEnabled
    ? {
        assumption_set_id: assumptionSetId,
        forecast_start_date: forecastStartDate,
        forecast_end_date: forecastEndDate,
      }
    : {};

  const { data: financialMetrics } = useGetFinancialMetricsQuery(
    {
      entityId,
      start_date: filter.START_DATE,
      end_date: filter.END_DATE,
      is_custom_date_range: isCustomDateRange || undefined,
    },
    { skip: !entityId || !exportSections.overview }
  );
  const pageRefs = useRef<(HTMLDivElement | null)[]>([]);

  const [exportFinancialSummary] = useExportFinancialSummaryMutation();
  const [getPreviewUrl] = useLazyGetPreviewUrlQuery();

  const {
    data: arData,
    isFetching: arFetching,
    isSuccess: arSuccess,
    isError: arError,
  } = useGetAccountsReceivableAnalysisQuery(
    { entityId, start_date: filter.START_DATE, end_date: filter.END_DATE },
    {
      skip:
        !entityId ||
        (!exportSections.accountsReceivable && !exportSections.arAging),
    }
  );

  const {
    data: apData,
    isFetching: apFetching,
    isSuccess: apSuccess,
    isError: apError,
  } = useGetAccountsPayableAnalysisQuery(
    { entityId, start_date: filter.START_DATE, end_date: filter.END_DATE },
    {
      skip:
        !entityId ||
        (!exportSections.accountsPayable && !exportSections.apAging),
    }
  );

  const { isFetching: cashBalanceFetching } = useGetCashBalanceAnalysisQuery(
    { entityId, start_date: filter.START_DATE, end_date: filter.END_DATE },
    { skip: !entityId || !exportSections.cashBalance }
  );

  const { isFetching: revenueAnalysisFetching } = useGetAnalysisQuery(
    {
      entityId,
      start_date: filter.START_DATE,
      end_date: filter.END_DATE,
      report_type: "REVENUE",
      analysis_type: analysisTypes.revenueAnalysisType,
      is_custom_date_range: isCustomDateRange || undefined,
    },
    { skip: !entityId || !exportSections.revenueAnalysis }
  );

  const { isFetching: spendAnalysisFetching } = useGetAnalysisQuery(
    {
      entityId,
      start_date: filter.START_DATE,
      end_date: filter.END_DATE,
      report_type: "SPEND",
      analysis_type: analysisTypes.spendAnalysisType,
      is_custom_date_range: isCustomDateRange || undefined,
    },
    { skip: !entityId || !exportSections.spendAnalysis }
  );

  const { isFetching: revMoMFetching } = useGetMoMBreakdownQuery(
    {
      entityId,
      start_date: filter.START_DATE,
      end_date: filter.END_DATE,
      report_type: "REVENUE",
      ...forecastParams,
    },
    { skip: !entityId || !exportSections.revenueBreakdown }
  );

  const { isFetching: expMoMFetching } = useGetMoMBreakdownQuery(
    {
      entityId,
      start_date: filter.START_DATE,
      end_date: filter.END_DATE,
      report_type: "EXPENSE",
      ...forecastParams,
    },
    { skip: !entityId || !exportSections.expenseBreakdown }
  );

  const { isFetching: arAgingFetching } = useGetARAgingSummaryInsightsQuery(
    { entityId, start_date: filter.START_DATE, end_date: filter.END_DATE },
    { skip: !entityId || !exportSections.arAging }
  );

  const { isFetching: apAgingFetching } = useGetAPAgingSummaryInsightsQuery(
    { entityId, start_date: filter.START_DATE, end_date: filter.END_DATE },
    { skip: !entityId || !exportSections.apAging }
  );

  const { isFetching: profitabilityMetricsFetching } = useGetMetricsQuery(
    {
      entityId,
      burn_rate_start_date: filter.START_DATE,
      burn_rate_end_date: filter.END_DATE,
      categorisation_type: "ALL",
      is_custom_date_range: isCustomDateRange || undefined,
    },
    { skip: !entityId || !exportSections.profitability }
  );

  const { isFetching: profitabilityRevenueFetching } =
    useGetRevenueMetricsQuery(
      {
        groupId,
        entityId,
        start_date: filter.START_DATE,
        end_date: filter.END_DATE,
        categorisation_type: "ALL",
      },
      { skip: !entityId || !groupId || !exportSections.profitability }
    );

  const isAnyFetching =
    arFetching ||
    apFetching ||
    cashBalanceFetching ||
    revenueAnalysisFetching ||
    spendAnalysisFetching ||
    revMoMFetching ||
    expMoMFetching ||
    arAgingFetching ||
    apAgingFetching ||
    profitabilityMetricsFetching ||
    profitabilityRevenueFetching;

  const subtitle = `${dayjs(filter.START_DATE).format(DISPLAY_DATE)} – ${dayjs(
    filter.END_DATE
  ).format(DISPLAY_DATE)}`;

  const forecastSubtitle =
    isForecastEnabled && forecastStartDate && forecastEndDate
      ? `Forecast: ${dayjs(forecastStartDate).format(DISPLAY_DATE)} – ${dayjs(
          forecastEndDate
        ).format(DISPLAY_DATE)}`
      : null;

  const activeGroups = groupOrder.filter((g) =>
    isGroupVisible(g, exportSections)
  );

  const allSections: string[] = [];
  for (const groupKey of activeGroups) {
    if (groupKey === GROUP_KEYS.revenue) {
      if (exportSections.revenueAnalysis) allSections.push("revenueAnalysis");
      if (exportSections.revenueBreakdown || exportSections.topCustomers)
        allSections.push("revenueBreakdownRow");
    } else if (groupKey === GROUP_KEYS.expense) {
      if (exportSections.spendAnalysis) allSections.push("spendAnalysis");
      if (exportSections.expenseBreakdown || exportSections.topVendors)
        allSections.push("expenseBreakdownRow");
    } else {
      allSections.push(groupKey);
    }
  }

  const MAX_SECTIONS_PER_PAGE = 3;

  const pages: string[][] = [];
  for (let i = 0; i < allSections.length; i += MAX_SECTIONS_PER_PAGE) {
    pages.push(allSections.slice(i, i + MAX_SECTIONS_PER_PAGE));
  }
  if (pages.length === 0) pages.push([]);

  const triggerExport = useCallback(async () => {
    const elements = pageRefs.current.slice(0, pages.length);

    if (!entityId || !groupId || elements.some((el) => !el)) {
      onComplete(false);
      return;
    }

    const captureElement = async (element: HTMLElement): Promise<string> => {
      const capturePromise = html2canvas(element, {
        scale: 1,
        useCORS: true,
        backgroundColor: "#ffffff",
      });
      const timeoutPromise = new Promise<never>((_, reject) =>
        setTimeout(() => reject(new Error("html2canvas timed out")), 30_000)
      );
      return Promise.race([capturePromise, timeoutPromise]).then(
        (canvas) => canvas.toDataURL("image/png").split(",")[1]
      );
    };

    try {
      const base64Images: string[] = [];
      for (const element of elements) {
        const image = await captureElement(element!);
        base64Images.push(image);
      }

      if (onCaptured) {
        onCaptured(base64Images);
        return;
      }

      const { document_id } = await exportFinancialSummary({
        groupId,
        entityId,
        graphs: base64Images,
      }).unwrap();

      const { download_url } = await getPreviewUrl({
        groupId,
        fileId: document_id,
      }).unwrap();

      openLink(download_url, "_blank");
      successToast({
        title: "Successfully downloaded",
        message: "Financial summary",
      });
      onComplete(true);
    } catch (error) {
      onComplete(false);
    }
  }, [
    entityId,
    groupId,
    pages.length,
    exportFinancialSummary,
    getPreviewUrl,
    onCaptured,
    onComplete,
    successToast,
  ]);

  const triggerExportRef = useRef(triggerExport);
  triggerExportRef.current = triggerExport;

  useEffect(() => {
    if (isAnyFetching) return;

    const timer = setTimeout(() => {
      triggerExportRef.current();
    }, CAPTURE_DELAY_MS);

    return () => clearTimeout(timer);
  }, [isAnyFetching]);

  const metrics = buildSummaryMetrics(financialMetrics, periodText);

  const renderSection = (sectionKey: string) => {
    switch (sectionKey) {
      case GROUP_KEYS.cashHealth:
        return (
          <>
            {exportSections.overview && (
              <SummaryMetricsCard metrics={metrics} />
            )}
            {exportSections.cashBalance && (
              <CashBalance
                startDate={filter.START_DATE}
                endDate={filter.END_DATE}
              />
            )}
          </>
        );

      case GROUP_KEYS.profitLoss:
        return (
          <CashFlow
            categorisationType="ALL"
            groupId={groupId}
            period={{
              startDate: filter.START_DATE,
              endDate: filter.END_DATE,
              periodText,
              isCustomDateRange,
            }}
            {...forecastParams}
          />
        );

      case "revenueAnalysis":
        return (
          <RevenueAnalysis
            startDate={filter.START_DATE}
            endDate={filter.END_DATE}
            periodText={periodText}
            reportType="REVENUE"
            forcedAnalysisType={analysisTypes.revenueAnalysisType}
            isCustomDateRange={isCustomDateRange}
          />
        );

      case "revenueBreakdownRow":
        if (isForecastEnabled) {
          return (
            exportSections.revenueBreakdown && (
              <div className="t-h-96">
                <MoMBreakdown
                  startDate={filter.START_DATE}
                  endDate={filter.END_DATE}
                  reportType="REVENUE"
                  title="MoM Revenue Breakdown"
                  {...forecastParams}
                />
              </div>
            )
          );
        }
        return (
          <div className="t-flex t-gap-6 t-h-96">
            {exportSections.revenueBreakdown && (
              <div className="t-flex-1">
                <MoMBreakdown
                  startDate={filter.START_DATE}
                  endDate={filter.END_DATE}
                  reportType="REVENUE"
                  title="MoM Revenue Breakdown"
                />
              </div>
            )}
            {exportSections.topCustomers && (
              <div className="t-flex-1">
                <TopCustomers
                  startDate={filter.START_DATE}
                  endDate={filter.END_DATE}
                  groupId={groupId}
                  isExport
                />
              </div>
            )}
          </div>
        );

      case "spendAnalysis":
        return (
          <RevenueAnalysis
            startDate={filter.START_DATE}
            endDate={filter.END_DATE}
            periodText={periodText}
            reportType="SPEND"
            forcedAnalysisType={analysisTypes.spendAnalysisType}
            isCustomDateRange={isCustomDateRange}
          />
        );

      case "expenseBreakdownRow":
        if (isForecastEnabled) {
          return (
            exportSections.expenseBreakdown && (
              <div className="t-h-96">
                <MoMBreakdown
                  startDate={filter.START_DATE}
                  endDate={filter.END_DATE}
                  reportType="EXPENSE"
                  title="MoM Expense Breakdown"
                  {...forecastParams}
                />
              </div>
            )
          );
        }
        return (
          <div className="t-flex t-gap-6 t-h-96">
            {exportSections.expenseBreakdown && (
              <div className="t-flex-1">
                <MoMBreakdown
                  startDate={filter.START_DATE}
                  endDate={filter.END_DATE}
                  reportType="EXPENSE"
                  title="MoM Expense Breakdown"
                />
              </div>
            )}
            {exportSections.topVendors && (
              <div className="t-flex-1">
                <TopVendors
                  startDate={filter.START_DATE}
                  endDate={filter.END_DATE}
                  groupId={groupId}
                  isExport
                />
              </div>
            )}
          </div>
        );

      case GROUP_KEYS.ar:
        return (
          <div className="t-flex t-gap-6 t-items-stretch">
            {exportSections.accountsReceivable && (
              <div className="t-flex-1">
                <AccountsReceivable
                  data={arData}
                  isLoading={arFetching}
                  isSuccess={arSuccess}
                  isError={arError}
                  forcedTab={analysisTypes.arViewType}
                  isExport
                />
              </div>
            )}
            {exportSections.arAging && (
              <div className="t-flex-1">
                <ARAgingCard
                  startDate={filter.START_DATE}
                  endDate={filter.END_DATE}
                />
              </div>
            )}
          </div>
        );

      case GROUP_KEYS.ap:
        return (
          <div className="t-flex t-gap-6 t-items-stretch">
            {exportSections.accountsPayable && (
              <div className="t-flex-1">
                <AccountsPayable
                  data={apData}
                  isLoading={apFetching}
                  isSuccess={apSuccess}
                  isError={apError}
                  forcedTab={analysisTypes.apViewType}
                  isExport
                />
              </div>
            )}
            {exportSections.apAging && (
              <div className="t-flex-1">
                <APAgingCard
                  startDate={filter.START_DATE}
                  endDate={filter.END_DATE}
                />
              </div>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div>
      {pages.map((pageGroups, pageIndex) => (
        <div
          key={pageIndex}
          ref={(el) => {
            pageRefs.current[pageIndex] = el;
          }}
          className="t-flex t-flex-col t-gap-6 t-px-9 t-py-6 t-bg-white"
        >
          {pageIndex === 0 && (
            <div className="t-flex t-flex-col t-items-center t-gap-1 t-pt-2 t-pb-4">
              <h1 className="t-text-h3 t-font-semibold t-text-text-100">
                Financial Summary
              </h1>
              <span className="t-text-body-sm t-text-text-30">{subtitle}</span>
              {forecastSubtitle && (
                <span className="t-text-body-sm t-text-text-30">
                  {forecastSubtitle}
                </span>
              )}
            </div>
          )}

          {pageGroups.map((groupKey) => (
            <div key={groupKey} className="t-flex t-flex-col t-gap-6">
              {renderSection(groupKey)}
            </div>
          ))}
        </div>
      ))}
    </div>
  );
};
